import { lazy, useState, useRef } from 'react';
import apiFetch from '@wordpress/api-fetch';
import {
    BlockWrapper,
    ChildrenBlock,
    defineBlock,
    smartProps,
    useEditor,
    useInspectorControl,
} from '@smartfire/js-block-engine';
import { useMediaQuery } from 'react-responsive';
import Button from '../button/block.js';
import style from './block.module.scss';
import clsx from 'clsx';

const Inspector = lazy(() => import('./inspector'));

const Block = ({ formId, formVersion, sendButtonText, width, formAlign }) => {
    useInspectorControl(Inspector);
    const { isEditing } = useEditor();
    const isMobile = useMediaQuery({ query: '(max-width: ' + style.mobileBreakpoint + ')' });
    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState('');
    const [formErrors, setFormErrors] = useState([]);
    const formRef = useRef(null);

    const sendForm = (e) => {
        e.preventDefault();

        if (isLoading) return;
        if (isEditing) return;
        if (!formRef?.current) return;

        setFormErrors([]);

        const errors = [];
        const formData = new FormData();
        formData.append('formId', formId);
        formData.append('formVersion', formVersion);

        for (const inputElement of formRef.current.elements) {
            if (inputElement.tagName === 'BUTTON') continue;
            if (inputElement.type === 'radio' && !inputElement.checked) continue;

            if (inputElement.tagName === 'INPUT') {
                const groupElement = inputElement.closest('[id^="formulaide_group"]');

                if (groupElement) {
                    const groupStyle = window.getComputedStyle(groupElement);
                    if (groupStyle.getPropertyValue('display') === 'none') continue;
                }

                if (inputElement.type !== 'hidden' && inputElement.value.trim() === '') {
                    errors.push({ field: inputElement.name, message: 'Ce champ est requis.' });
                }

                if (inputElement.type === 'hidden' && inputElement.name === 'arity_min_elements') continue;

                if (inputElement.type === 'tel') {
                    const frenchPhoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
                    if (!frenchPhoneRegex.test(inputElement.value)) {
                        errors.push({ field: inputElement.name, message: 'Le numéro de téléphone est invalide.' });
                    }
                }

                if (inputElement.type === 'email') {
                    const emailRegex = /^[A-Za-z0-9_!#$%&'*+=?`{|}~^.-]+@[A-Za-z0-9.-]+$/gm;
                    if (!emailRegex.test(inputElement.value)) {
                        errors.push({ field: inputElement.name, message: "L'adresse email est invalide." });
                    }
                }

                if (inputElement.type === 'checkbox') {
                    inputElement.value = inputElement.checked ? 'true' : 'false';
                }
            }

            formData.append(inputElement.name, inputElement.value);
        }

        const arityElements = formRef.current.querySelectorAll('[id^="formulaide_arity_"]');

        if (arityElements.length > 0) {
            for (const arityElement of arityElements) {
                const groupElement = arityElement.closest('[id^="formulaide_group"]');

                if (groupElement) {
                    const groupStyle = window.getComputedStyle(groupElement);
                    if (groupStyle.getPropertyValue('display') === 'none' && groupElement.style.display !== 'block') {
                        continue;
                    }
                }

                const arityMinElements = arityElement.querySelector('[name="arity_min_elements"');
                const arityMin = arityMinElements.value;

                if (arityMin < 1) continue;

                const arityChildrenCount = arityElement.querySelector('[id^=arity_groups_]').childElementCount;

                if (arityChildrenCount < arityMin) {
                    errors.push({ field: arityElement.id, message: 'Vous n’avez pas ajouté assez d’éléments.' });
                }
            }
        }

        setFormErrors(errors);

        if (errors.length > 0) return;

        setIsLoading(true);

        apiFetch({
            path: 'smartfire/v1/sendDataToForm',
            method: 'POST',
            body: formData,
        })
            .then((res) => {
                const inputs = formRef.current.querySelectorAll('input');

                if (inputs.length > 0) {
                    inputs.forEach((input) => {
                        if (input.type !== 'radio') input.value = '';
                    });
                }

                setFormMessage(res?.message);
            })
            .catch(() => {
                setFormMessage("Désolé, une erreur s'est produite.");
            })
            .finally(() => {
                setIsLoading(false);
                const { top, bottom } = formRef.current.getBoundingClientRect();
                const windowHeight = window.innerHeight;

                if (top < 0 || bottom > windowHeight) {
                    formRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            });
    };

    return (
        <BlockWrapper.Form className={clsx(style.form, style[formAlign])} onSubmit={sendForm} ref={formRef}>
            {formMessage && (
                <div className={style.alert}>
                    <p>{formMessage}</p>
                </div>
            )}

            <ChildrenBlock
                gutenbergEditorConfig={{
                    orientation: 'vertical',
                }}
                className={clsx(style.fields, style[width])}
                childrenProps={{
                    formErrors: formErrors,
                    setFormErrors: setFormErrors,
                }}
            />

            <Button
                text={sendButtonText}
                className={style.sendButton}
                showInspector={false}
                align={isMobile ? 'center' : 'left'}
                isSubmit={true}
                tagName={'button'}
                isLoading={isLoading}
            />
        </BlockWrapper.Form>
    );
};

export default defineBlock({
    Block: Block,
    smartProps: {
        formId: smartProps.string.withDefault(''),
        formVersion: smartProps.string.withDefault(''),
        formFields: smartProps.array.withDefault({}).contentServerSide(),
        sendButtonText: smartProps.string.withDefault('Envoyer le formulaire'),
        width: smartProps.string.withDefault('medium'),
        formAlign: smartProps.string.withDefault('left'),
    },
    setup: {
        title: 'Formulaide',
    },
});
